import { Button, Stack } from '@mui/material';
import React, { Dispatch } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { IAppReduxStore } from '../../../redux/store';
import Theme, { headingFontBold } from '../../../Theme';
import {
    fetchImportDocuments,
    fetchImportStructure,
    fetchImportViews,
} from '../../../util/ImportUtils';
import { getCurrentFieldsOrFieldsSchema } from '../../../redux/project/sliceProjectViewData';
import { getProjectAdvancedSearchDocuments } from '../../../redux/project/documents/sliceProjectAdvancedSearch';
import { getAllFields } from '../../../util/DataUtils';
import { getCurrentView } from '../../../redux/project/sliceProjectViews';
import { fetchDocuments } from '../../../util/ProjectUtils';

/**
 * Component that shows the user an message when an error occurs loading their documents.
 */
export default () => {
    /** Configure component translation. */
    const { t } = useTranslation('translations', { keyPrefix: 'docTable' });

    /** Allows reducers on a redux slice to be called. */
    const dispatch: Dispatch<unknown> = useDispatch();

    const params = useParams();
    /** Access replicaId, areaName, viewName and category data from the URL. */
    const { replicaId, areaName, viewName } = params;

    /** Project data */
    const fields = useSelector(getCurrentFieldsOrFieldsSchema('fields'));
    const view = useSelector(getCurrentView({ areaName, viewName }));

    /** Import data */
    const importViews = useSelector((store: IAppReduxStore) => store.importViews);
    const importStructures = useSelector(
        (store: IAppReduxStore) => store.importViewData.viewStructures,
    );
    const importDocs = useSelector((store: IAppReduxStore) => store.importDocuments);
    const { areaSearchText } = useSelector((store: IAppReduxStore) => store.projectAdvancedSearch);

    const reloadImportData = () => {
        if (importViews.error) {
            fetchImportViews(replicaId);
        }
        if (importStructures.error) {
            importViews.data?.map(importView => {
                importView.preload && fetchImportStructure(replicaId, importView.name);
            });
        }
        if (importDocs.error) {
            fetchImportDocuments(replicaId);
        }
    };

    const reloadSearchData = () => {
        dispatch(
            getProjectAdvancedSearchDocuments({
                replicaId,
                viewName: view.viewStructure,
                searchTerm: areaSearchText,
                fieldset: getAllFields(fields).join(';'),
            }),
        );
    };

    const triggerReload = React.useCallback(() => {
        if (areaName === 'import' && !areaSearchText) {
            reloadImportData();
            return;
        }

        if (areaSearchText) {
            reloadSearchData();
            return;
        }

        fetchDocuments(params);
    }, [areaName]);

    return (
        <Stack
            spacing={2}
            sx={{
                alignItems: 'center',
                justifyContent: 'center',
                flexGrow: 4,
            }}
        >
            <Stack sx={{ ...headingFontBold, fontSize: 30, color: Theme.palette.error.main }}>
                {t('error')}
            </Stack>
            <Stack sx={{ ...headingFontBold }}>{t('documentLoadError')}</Stack>
            <Button
                color='error'
                sx={{ ...headingFontBold, textTransform: 'none', px: 2 }}
                onClick={triggerReload}
            >
                {t('reload')}
            </Button>
        </Stack>
    );
};
